import React from 'react';
import type { Journey, JourneyStepView } from '../../types';

interface JourneyProgressProps {
  journey: Journey;
}

/**
 * JourneyProgress (AC-1756, epic AC-1749 Assistant Concierge).
 *
 * A presentational strip that surfaces the user's goal journey position — a row
 * of step chips (done / current / remaining / blocked), framed around the user's
 * goal so progress reads as "toward summarize your emails", not a bare list of
 * raw step ids.
 *
 * Conventions (mirrored from PreviewCard / McpTrustSignals):
 *   - Every step status is conveyed AS TEXT, never color-only (accessibility).
 *   - A declined dependency surfaces a blocked banner (role="status") with the
 *     reason text verbatim.
 *   - A completed journey shows an "all set" complete state.
 *
 * Purely derived from props — no Zustand, no React Query. It can be rendered bare
 * (no provider wrapper). AssistantChat owns the wiring (a later ticket): it reads
 * response.journey, stores it on the message, and renders this strip above the
 * pending-action card slot.
 */

/** Textual (not color-only) status cue for each step status. */
const STATUS_TEXT: Record<JourneyStepView['status'], string> = {
  done: 'Done',
  current: 'Current',
  remaining: 'To do',
  blocked: 'Blocked',
};

/** Tailwind chip styling per status (the color is supplementary to the text). */
const STATUS_CHIP_CLASS: Record<JourneyStepView['status'], string> = {
  done: 'border-green-300 bg-green-50 text-green-800 dark:border-green-800 dark:bg-green-900/30 dark:text-green-300',
  current:
    'border-blue-400 bg-blue-50 text-blue-800 font-medium dark:border-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  remaining:
    'border-gray-200 bg-gray-50 text-gray-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400',
  blocked:
    'border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
};

const JourneyProgress: React.FC<JourneyProgressProps> = ({ journey }) => {
  const { goal_label, steps, complete, blocked_reason } = journey;

  return (
    <div className="my-2 rounded-md border border-gray-200 bg-white p-3 text-sm dark:border-gray-700 dark:bg-gray-900">
      {/* Goal framing: progress is toward the GOAL, not a raw step list. */}
      <p className="mb-2 text-xs text-gray-600 dark:text-gray-400">
        {complete ? (
          <span>All set — ready to {goal_label}.</span>
        ) : (
          <span>
            Getting set up to <span className="font-medium">{goal_label}</span>
          </span>
        )}
      </p>

      {/* The row of step chips, one per journey step. */}
      <ol className="flex flex-wrap items-center gap-2">
        {steps.map((step, index) => (
          <li
            key={`${step.kind}-${index}`}
            className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs ${
              STATUS_CHIP_CLASS[step.status]
            }`}
          >
            <span aria-hidden="true">
              {step.status === 'done'
                ? '✓'
                : step.status === 'blocked'
                ? '⚠'
                : `${index + 1}.`}
            </span>
            <span>{step.label}</span>
            {/* Status as text (screen-reader friendly, never color-only). */}
            <span className="ml-0.5 text-[10px] uppercase tracking-wide opacity-80">
              {STATUS_TEXT[step.status]}
            </span>
          </li>
        ))}
      </ol>

      {/* Blocked banner: the plain-language stop reason, announced as a status. */}
      {blocked_reason && (
        <div
          role="status"
          className="mt-2 rounded border border-amber-300 bg-amber-50 px-2 py-1.5 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-900/30 dark:text-amber-300"
        >
          {blocked_reason}
        </div>
      )}
    </div>
  );
};

export default JourneyProgress;
